import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, FeatureBlockedError, type FeatureBlock } from "./apiClient";

export type { FeatureBlock };

export interface LicenseInfo {
  customerId: string;
  customerName: string;
  tier: string;
  validFrom: string | null;
  validTo: string | null;
  activeModules: string[];
  valid: boolean;
  hasRuntimeActivation: boolean;
  runtimeActivationExpiry: string | null;
}

export interface ActivationResult {
  success: boolean;
  message: string | null;
  activatedModules?: string[] | null;
  expiresAt?: string | null;
}

export function useLicenseInfo() {
  return useQuery({ queryKey: ["license", "info"], queryFn: () => apiFetch<LicenseInfo>("/api/v1/license/info") });
}

export function useActivateLicense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (licenseKey: string) =>
      apiFetch<ActivationResult>("/api/v1/license/activate", { method: "POST", json: { licenseKey } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["license"] });
      qc.invalidateQueries({ queryKey: ["settings", "ui", "navigation"] });
    },
  });
}

/**
 * Feature-gating — backend-driven přes `/api/v1/license/features`,
 * `/admin/api/v1/connectors/status`, `/api/v1/settings/ui/navigation`.
 *
 * KRITICKÉ (default-deny): `/features` NEvrací HIDDEN features. Cokoliv, co
 * není v odpovědi, se považuje za HIDDEN. Každý gate MUSÍ defaultovat na skryté.
 */
export type FeatureVisibility = "ENABLED" | "ADVERTISED" | "LOCKED" | "HIDDEN";

export interface FeatureDto {
  id: string;
  name?: string;
  module?: string;
  tier?: string;
  visibility: FeatureVisibility;
  plannedVersion?: string | null;
  enabled?: boolean;
  visible?: boolean;
  shortDescription?: string | null;
  fullDescription?: string | null;
  advertisingText?: string | null;
}
interface FeaturesResponse {
  features: FeatureDto[];
}

export function useFeatures() {
  return useQuery({
    queryKey: ["license", "features"],
    queryFn: () => apiFetch<FeaturesResponse>("/api/v1/license/features", { skipAuthRedirect: true }),
    staleTime: 5 * 60_000,
  });
}

export function useConnectorStatus() {
  return useQuery({
    queryKey: ["connector-config", "status"],
    queryFn: () => apiFetch<Record<string, boolean>>("/admin/api/v1/connectors/status", { skipAuthRedirect: true }),
    staleTime: 5 * 60_000,
  });
}

export function useNavVisibility() {
  return useQuery({
    queryKey: ["settings", "ui", "navigation"],
    queryFn: () => apiFetch<Record<string, boolean>>("/api/v1/settings/ui/navigation", { skipAuthRedirect: true }),
    staleTime: 5 * 60_000,
  });
}

/** Default-deny lookup: chybějící feature = HIDDEN. */
export function visibilityOf(features: FeaturesResponse | undefined, featureId: string): FeatureVisibility {
  const found = features?.features.find((f) => f.id === featureId);
  return found?.visibility ?? "HIDDEN";
}

/**
 * Patří položka do navigace? ADVERTISED i LOCKED ano — právě proto se inzerují.
 * Neznamená to, že se smí otevřít obsah; na to je {@link isEnabled}.
 */
export function isNavVisible(v: FeatureVisibility): boolean {
  return v === "ENABLED" || v === "ADVERTISED" || v === "LOCKED";
}

/** @deprecated Přejmenováno na {@link isNavVisible} — název sváděl k použití jako gate obsahu. */
export const isVisible = isNavVisible;

/** Smí se obsah vyrenderovat? Jen ENABLED. */
export function isEnabled(v: FeatureVisibility): boolean {
  return v === "ENABLED";
}

/** Celý záznam feature (kvůli advertisingText / plannedVersion na gate stránce). */
export function featureOf(features: FeaturesResponse | undefined, featureId: string): FeatureDto | undefined {
  return features?.features.find((f) => f.id === featureId);
}

/** Viditelnost + metadata jedné feature; `isLoading` odliší "ještě nevíme" od "skryté". */
export function useFeature(featureId: string): {
  visibility: FeatureVisibility;
  feature?: FeatureDto;
  isLoading: boolean;
} {
  const features = useFeatures();
  return {
    visibility: visibilityOf(features.data, featureId),
    feature: featureOf(features.data, featureId),
    isLoading: features.isLoading,
  };
}

/**
 * Vytáhne feature-block z chyby dotazu. Použij tam, kde routa gate nemá (nebo
 * kdyby se gate rozešel s backendem) — stránka pak umí v `isError` větvi
 * vykreslit vysvětlení místo obecné chyby.
 */
export function parseFeatureError(error: unknown): FeatureBlock | null {
  return error instanceof FeatureBlockedError ? error.block : null;
}

/** Nav-item viditelnost (admin toggle): default zobrazit, skrýt jen když explicitně false. */
export function navVisible(nav: Record<string, boolean> | undefined, navKey: string): boolean {
  return nav?.[navKey] !== false;
}
